/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    OperatorDetailsActions,
    OperatorDetailsActionType,
    fetchOperatorCardDataErrorAction,
    fetchOperatorDetailsSuccessAction,
    fetchOperatorCashRegistersSuccessAction,
    fetchOperatorCashboxesStatsSuccessAction,
} from '../actions/operatorDetailsActons';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { operatorsClient } from '../api/operators/operatorsClient';
import { cashRegistersClient } from '../api/cashRegisters/cashRegistersClient';
import { CashRegisterModelsListRequestParams } from '../api/cashRegisters/cashRegistersClient.typings';
import { OperatorDetailsState } from '../reducers/operatorDetailsReducer';
import { AxiosError } from 'axios';
import { parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';

export const fetchOperatorDetailsEpic: Epic<OperatorDetailsActions> = (action$, state) => {
    return action$.pipe(
        ofType(OperatorDetailsActionType.OPEN_REGISTRY_OPERATOR_CARD_PAGE),
        switchMap(() => {
            const operatorTaxNumber = state.value.routerState.route.params.OperatorTaxNumber;

            return from(operatorsClient.getOperator(operatorTaxNumber)).pipe(
                map((resp) => fetchOperatorDetailsSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchOperatorCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.operatorNotFound', { searchText: operatorTaxNumber }),
                            ),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(OperatorDetailsActionType.EXIT_REGISTRY_OPERATOR_CARD_PAGE)),
            );
        }),
    );
};

export const fetchOperatorCashBoxesStatsEpic: Epic<OperatorDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(OperatorDetailsActionType.OPEN_REGISTRY_OPERATOR_CARD_PAGE),
        switchMap(() => {
            const operatorTaxNumber = state$.value.routerState.route.params.OperatorTaxNumber;

            return from(operatorsClient.getOperatorCashBoxesStats(operatorTaxNumber)).pipe(
                map((resp) => {
                    return fetchOperatorCashboxesStatsSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchOperatorCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashBoxesStatsNotFound')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(OperatorDetailsActionType.EXIT_REGISTRY_OPERATOR_CARD_PAGE)),
            );
        }),
    );
};

export const fetchOperatorCashRegistersEpic: Epic<OperatorDetailsActions> = (action$, state) => {
    return action$.pipe(
        ofType(
            OperatorDetailsActionType.OPEN_REGISTRY_OPERATOR_CARD_PAGE,
            OperatorDetailsActionType.APPLY_CASH_REGISTERS_PAGING,
            OperatorDetailsActionType.APPLY_CASH_REGISTERS_FILTER,
            OperatorDetailsActionType.APPLY_CASH_REGISTERS_SORTING,
        ),
        switchMap(() => {
            const queryParams = makeCashRegistersListQueryParamsFromState(state.value.operatorDetailsReducer);
            queryParams.operatorTaxNumber = state.value.routerState.route.params.OperatorTaxNumber;

            return from(cashRegistersClient.listCashRegistersModels(queryParams)).pipe(
                map((list) => fetchOperatorCashRegistersSuccessAction(list)),
                catchError((err: AxiosError) =>
                    of(
                        fetchOperatorCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashRegistersModelsListReqFailed')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(OperatorDetailsActionType.EXIT_REGISTRY_OPERATOR_CARD_PAGE)),
            );
        }),
    );
};

function makeCashRegistersListQueryParamsFromState(state: OperatorDetailsState): CashRegisterModelsListRequestParams {
    return {
        sort: state.modelsSortParams.sort,
        offset: state.modelsPageParams.offset,
        limit: state.modelsPageParams.limit,
        name: state.modelsFilterParams.name,
        status: state.modelsFilterParams.status ? [state.modelsFilterParams.status] : [],
    };
}
